'use client';

import { Container, CustomImage } from '..';
import { PLargeGradient } from '../blood-vision/styleConstants';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useRef } from 'react';
import gsap from 'gsap';
import { CaretUp } from 'assets/icons';
import { ContainerL } from './ContainerL';
import CustomLink from 'components/shared/CustomLink';
import styles from './AboutPerfectFit.module.css';

export const AboutPerfectFit = () => {
  const intersectionObserver = useRef<IntersectionObserver | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);

  const gsapContext = useGsapContext();

  useIsomorphicLayoutEffect(() => {
    if (!rootRef.current) return;

    intersectionObserver.current = new IntersectionObserver(
      () => {
        gsapContext.add(() => {
          const tl = gsap.timeline({
            defaults: {
              duration: 1,
            },
            scrollTrigger: {
              immediateRender: false,
              trigger: rootRef.current,
              start: 'top 90%',
              end: 'bottom 60%',
              scrub: 0.2,
              invalidateOnRefresh: true,
            },
          });

          tl.set(`.tc-1 > span`, {
            autoAlpha: 0.7,
          });

          tl.fromTo(
            '.tc-1 > span',
            {
              autoAlpha: 0.7,
            },
            {
              autoAlpha: 1,
              duration: 0.35,
              stagger: 0.2,
            },
            0.2,
          );
          tl.addPause(3);
        });
      },
      {
        root: null,
        rootMargin: '20px',
        threshold: 0.01,
      },
    );

    intersectionObserver.current.observe(rootRef.current);

    return () => {
      intersectionObserver.current?.disconnect();
    };
  }, []);

  return (
    <section className={styles.root}>
      <Container className={styles.content} ref={rootRef}>
        {/* literal tc-1 class is the GSAP selector hook — keep it */}
        <PLargeGradient className={`${styles.textContent} tc-1`}>
          <span>Download</span>
          <span> the</span>
          <span> CAD</span>
          <span> files</span>
          <span> and</span>
          <span> import</span>
          <span> them</span>
          <span> into</span>
          <span> your</span>
          <span> 3D</span>
          <span> printing</span>
          <span> software.</span>
          <span> Print</span>
          <span> various</span>
          <span> ring</span>
          <span> sizes</span>
          <span> you</span>
          <span> prefer</span>
          <span> and</span>
          <span> wear</span>
          <span> them</span>
          <span> overnight</span>
          <span> to</span>
          <span> find</span>
          <span> your</span>
          <span> ideal</span>
          <span> fit.</span>
          <span> Then,</span>
          <span> go</span>
          <span> ahead</span>
          <span> and</span>
          <span> purchase</span>
          <span> the</span>
          <span> ring</span>
          <span> in</span>
          <span> the</span>
          <span> size</span>
          <span> that</span>
          <span> feels</span>
          <span> just</span>
          <span> right</span>
          <span> for</span>
          <span> you.</span>
        </PLargeGradient>
        <div className={styles.disclaimer}>
          <span className={styles.disclaimerTitle}>Just a heads up:</span> The
          accuracy of the printed STL files may vary slightly depending on
          your 3D printer type, resolution, and settings. For optimal results,
          we recommend using an SLA, LCD, or DLP printer at the highest
          resolution available. Additionally, we have included drawing and
          step files to assist you in measuring the inner dimensions to
          confirm print accuracy.
        </div>
      </Container>

      <ContainerL className={styles.cards}>
        <div className={styles.card}>
          {/* both crops render; CSS shows one per viewport (useWindowSize is 0 on SSR) */}
          <CustomImage
            className={styles.imgDesktop}
            src="web_v2/sizing-kit/sizingKitImgA.png"
            alt=""
            fill
          />
          <CustomImage
            className={styles.imgMobile}
            src="web_v2/sizing-kit/sizingKitA_resp.png"
            alt=""
            fill
          />
          <div className={styles.desc}>
            <p>
              Try on a sample ring that fits snugly, preferably on your index
              or middle finger.
            </p>
          </div>
        </div>
        <div className={styles.card}>
          <CustomImage
            className={styles.imgDesktop}
            src="web_v2/sizing-kit/sizingKitImgB.png"
            alt=""
            fill
          />
          <CustomImage
            className={styles.imgMobile}
            src="web_v2/sizing-kit/sizingKitB_resp.png"
            alt=""
            fill
          />
          <div className={styles.desc}>
            <span>Wear the ring overnight to test the fit.</span>
            <CustomLink
              href="/blog/how-to-use-the-ultrahuman-ring-sizing-kit/"
              className="redirect-to-blog"
            >
              <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {' '}
                Learn more about finding the best fit
              </span>{' '}
              <CaretUp fill="rgba(255, 255, 255, 0.7)" className={styles.caret} />
            </CustomLink>
          </div>
        </div>
      </ContainerL>
    </section>
  );
};
